import { useState, useEffect } from 'react'
import { InfoIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { useIsPointCentralBuildingConnected } from '@/api/property-keyless/point-central/use-is-point-central-building-connected'
import { Alert } from '@/components/ui/alert'
import { Tooltip } from '@/components/ui/tooltip'

import API from '../../../../api/api'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import Loader from '../../../../components/Loader/Loader'

const PointCentralSection = ({ isAvailable, buildingId, clientId }) => {
  const { t } = useTranslation(['clients-legacy', 'clients'])
  const { displaySnackbar } = useSnackbar()
  const { isBuildingConnected, isLoading: isLoadingBuildingConnection } =
    useIsPointCentralBuildingConnected(buildingId)
  const [isLoading, setIsLoading] = useState(false)
  const [isSynced, setIsSynced] = useState(false)

  const fetchMappingStatus = async () => {
    try {
      const { data } = await API.getPointCentralClientMapping(buildingId, clientId)
      setIsSynced(data.isSynced)
    } catch (error) {
      console.error('PointCentral mapping status error:', error)
      // Note: reset rather than leave stale - a later failed re-fetch must not keep showing
      // a resident as synced from a prior success.
      setIsSynced(false)
    }
  }

  const handleDisconnectResident = async () => {
    setIsLoading(true)
    try {
      await API.disconnectPointCentralResident(buildingId, clientId)
      setIsSynced(false)
      displaySnackbar(t('clients-legacy:profileView.integrations.pointCentral.disconnectSuccess'))
    } catch (error) {
      console.error('PointCentral disconnect error:', error)
      displaySnackbar(
        t('clients-legacy:profileView.integrations.pointCentral.disconnectError'),
        true,
      )
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (isAvailable) {
      fetchMappingStatus()
    }
  }, [isAvailable, clientId])

  if (!isAvailable) return null

  if (isLoadingBuildingConnection) {
    return (
      <div className="information-element">
        <Loader />
      </div>
    )
  }

  if (!isBuildingConnected) {
    return (
      <div className="information-element">
        <Alert
          type="error"
          title={t('clients:keylessAccess.pointCentral.buildingNotConnected.title')}
          message={t('clients:keylessAccess.pointCentral.buildingNotConnected.message')}
        />
      </div>
    )
  }

  if (!isSynced) return null

  return (
    <div className="information-element">
      <div className="element-name">
        {t('clients-legacy:profileView.integrations.pointCentral.label')}
      </div>
      <div className="columns">
        <div className="column">
          <button
            className={`button button-primary ${isLoading ? 'is-loading' : ''}`}
            disabled={isLoading}
            onClick={handleDisconnectResident}
          >
            {t('clients:keylessAccess.pointCentral.disconnect.action')}
          </button>
          <Tooltip triggerIcon={InfoIcon}>
            <div>{t('clients-legacy:profileView.integrations.pointCentral.tooltip')}</div>
          </Tooltip>
        </div>
      </div>
    </div>
  )
}

export default PointCentralSection
